import { useState, useEffect, useCallback, useRef } from "react";

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const INITIAL_CHECK_DELAY = 10 * 1000; // 10 seconds after app load
const SNOOZE_DURATION = 60 * 60 * 1000; // 1 hour
const DEPLOY_GRACE_PERIOD = 10 * 60 * 1000; // 10 minutes — wait for secondary EB env to finish deploying

// A real version token: the build epoch (e.g. 1556797366304), a git sha,
// "local-dev", "v1.2.3" etc. Anything with whitespace or "<" (HTML) fails.
// Keep this client-side format in sync with the server's `/version` body and
// `X-Sked-Version` header contract so valid longer deploy identifiers are not rejected.
const VERSION_RE = /^[A-Za-z0-9._@/-]{1,128}$/;

// Require this many consecutive identical reads that differ from the initial
// version before prompting. Guards against a single odd read — a rolling
// deploy instance flap, a transient WAF/redirect blip. See SKE-3664.
const CONSECUTIVE_READS_TO_PROMPT = 2;

export interface VersionCheckDebugInfo {
  initialVersion: string | null;
  detectedVersion: string | null;
  lastStatus: number | null;
  lastContentType: string | null;
  lastRejectReason: string | null;
  lastCheckedAt: string | null;
}

const EMPTY_DEBUG: VersionCheckDebugInfo = {
  initialVersion: null,
  detectedVersion: null,
  lastStatus: null,
  lastContentType: null,
  lastRejectReason: null,
  lastCheckedAt: null,
};

interface FetchResult {
  /** A validated version token, or null if the response was not usable. */
  version: string | null;
  status: number | null;
  contentType: string | null;
  /** Why the response was rejected (null when accepted). */
  rejectReason: string | null;
}

/**
 * Fetches the current deployed version from the server's /version endpoint.
 * The server proxies to CDN (S3) to avoid CORS issues.
 *
 * Only a response that is genuinely a version is accepted: HTTP 200
 * and a body that looks like a version token. A Cloudflare WAF / challenge
 * page or a redirect to HTML carries none of those, so it is rejected
 * instead of being mistaken for a new version. See SKE-3664.
 */
async function fetchLatestVersion(): Promise<FetchResult> {
  try {
    // Add cache-busting query param to ensure fresh response
    const response = await fetch(`/version?t=${Date.now()}`, {
      cache: "no-store",
    });

    const status = response.status;
    const contentType = response.headers.get("content-type");

    if (!response.ok) {
      return { version: null, status, contentType, rejectReason: "http-error" };
    }

    // Server returns text/plain; reject HTML responses (WAF pages, error pages)
    if (contentType && contentType.toLowerCase().includes("text/html")) {
      return {
        version: null,
        status,
        contentType,
        rejectReason: "html-response",
      };
    }

    const rawBody = await response.text();
    const body = rawBody.trim();

    if (!body || !VERSION_RE.test(body)) {
      return {
        version: null,
        status,
        contentType,
        rejectReason: "bad-body",
      };
    }

    // X-Sked-Version is an *optional confirmation*. If present and
    // non-empty, it must match the body — that's the WAF/redirect-page
    // rejection path (those pages won't carry the header). If absent
    // (or empty / whitespace-only, which a misconfigured proxy might
    // inject), we still accept on the strength of status + text/plain
    // + version-regex: a CDN or reverse proxy that strips custom X-*
    // response headers must not silently disable the update prompt for
    // every user behind it.
    const headerRaw = response.headers.get("x-sked-version");
    const header = headerRaw === null ? null : headerRaw.trim();
    if (header !== null && header !== "" && header !== body) {
      return {
        version: null,
        status,
        contentType,
        rejectReason: "header-mismatch",
      };
    }

    return { version: body, status, contentType, rejectReason: null };
  } catch {
    // Network error / aborted — treat as "no info, skip".
    return {
      version: null,
      status: null,
      contentType: null,
      rejectReason: "network-error",
    };
  }
}

/**
 * Hook to check for new app versions and prompt users to reload.
 *
 * Checks for new versions:
 * - Every 5 minutes
 * - When the tab regains focus (visibility change)
 *
 * A new version must be seen on {@link CONSECUTIVE_READS_TO_PROMPT}
 * consecutive reads before the modal is shown. When the user clicks
 * "Later", checks are suppressed for 1 hour.
 *
 * Returns state and actions to show/dismiss an update modal, plus
 * `debugInfo` for diagnosing false prompts in the field.
 */
export function useVersionCheck() {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const [debugInfo, setDebugInfo] =
    useState<VersionCheckDebugInfo>(EMPTY_DEBUG);
  const initialVersion = useRef<string | null>(null);
  const hasInitialized = useRef(false);
  const snoozedUntil = useRef<number>(0);
  // Debounce state: the candidate "new" version and how many consecutive
  // reads have now reported it.
  const pendingVersion = useRef<string | null>(null);
  const pendingCount = useRef<number>(0);
  // Timestamp of the first mismatch detected — used to enforce a grace
  // period so the secondary EB environment has time to finish deploying.
  const firstMismatchAt = useRef<number | null>(null);
  // Once we prompt, freeze the diagnostic so a later poll's bad-body /
  // header-mismatch can't overwrite the read that actually confirmed the
  // update. Reset on dismiss.
  const debugFrozen = useRef(false);

  const isSnoozed = useCallback(() => Date.now() < snoozedUntil.current, []);

  const checkVersion = useCallback(async () => {
    if (isSnoozed()) return;

    // Outer guard: a version check is non-critical. Any throw inside this
    // function — from React state setters during teardown, the debounce
    // logic, etc. — must not propagate as an unhandled rejection from the
    // interval / visibility handler.
    try {
      const result = await fetchLatestVersion();

      if (!debugFrozen.current) {
        setDebugInfo({
          initialVersion: initialVersion.current,
          detectedVersion: result.version,
          lastStatus: result.status,
          lastContentType: result.contentType,
          lastRejectReason: result.rejectReason,
          lastCheckedAt: new Date().toISOString(),
        });
      }

      const latestVersion = result.version;
      if (!latestVersion) {
        return;
      }

      // Store initial version on first successful fetch
      if (!hasInitialized.current) {
        initialVersion.current = latestVersion;
        hasInitialized.current = true;
        console.debug("[VersionCheck] Initial version:", latestVersion);
        return; // Don't compare on initial fetch
      }

      if (latestVersion === initialVersion.current) {
        // Back to the known-good version — clear any pending candidate.
        pendingVersion.current = null;
        pendingCount.current = 0;
        firstMismatchAt.current = null;
        return;
      }

      // Differs from initial — accumulate consecutive identical reads.
      firstMismatchAt.current ??= Date.now();

      if (latestVersion === pendingVersion.current) {
        pendingCount.current += 1;
      } else {
        pendingVersion.current = latestVersion;
        pendingCount.current = 1;
        firstMismatchAt.current = Date.now();
      }

      if (
        pendingCount.current >= CONSECUTIVE_READS_TO_PROMPT &&
        Date.now() - firstMismatchAt.current! >= DEPLOY_GRACE_PERIOD
      ) {
        console.debug(
          "[VersionCheck] New version confirmed:",
          latestVersion,
          "vs",
          initialVersion.current,
          `(${pendingCount.current} consecutive reads)`
        );
        debugFrozen.current = true;
        setIsNewVersionAvailable(true);
      } else if (pendingCount.current >= CONSECUTIVE_READS_TO_PROMPT) {
        console.debug(
          "[VersionCheck] New version confirmed but within deploy grace period:",
          latestVersion,
          `(${Math.round((Date.now() - firstMismatchAt.current!) / 1000)}s of ${DEPLOY_GRACE_PERIOD / 1000}s)`
        );
      } else {
        console.debug(
          "[VersionCheck] New version candidate (awaiting confirmation):",
          latestVersion,
          "vs",
          initialVersion.current
        );
      }
    } catch (error) {
      console.warn("Version check failed:", error);
    }
  }, [isSnoozed]);

  const dismissUpdate = useCallback(() => {
    snoozedUntil.current = Date.now() + SNOOZE_DURATION;
    debugFrozen.current = false;
    // Intentionally do NOT reset pendingCount/pendingVersion: if the same
    // new version is still deployed when the snooze expires, one confirming
    // read is enough to re-prompt (count is already at the threshold). If
    // a different version appears, the existing flap-back logic resets the
    // counter before counting it.
    setIsNewVersionAvailable(false);
  }, []);

  const triggerReload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    // Initial check with delay to not impact app startup
    const initialTimeout = setTimeout(checkVersion, INITIAL_CHECK_DELAY);

    // Periodic check every 5 minutes
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check when tab regains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkVersion]);

  return {
    isNewVersionAvailable,
    dismissUpdate,
    triggerReload,
    debugInfo,
  };
}
